import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fromInput, toDateInput, toInput, PAYMENT_STATUSES, COLLECTION_STATUSES } from "@/lib/api";
import { Sel, Field } from "@/components/common";

const EMPTY = { company: "", contact_person: "", phone: "", email: "", location: "", business_type: "", vertical: "BMS", source: "Website",
  assigned_to: "", requirements: "", notes: "", deal_name: "", deal_value: "", probability: "", expected_close: "", next_action: "",
  next_follow_up: "", stage: "New Lead", tags: [], custom_fields: {}, payment_status: "Pending", collection_status: "Not Due",
  collected_amount: "", payment_due_date: "", recurring: false, recurring_value: "" };

export const LeadFormDialog = ({ open, onOpenChange, lead, onSaved }) => {
  const { meta, isAdmin, salesUsers } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [dups, setDups] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDups([]);
    setF(lead ? { ...EMPTY, ...lead, expected_close: toDateInput(lead.expected_close), next_follow_up: toInput(lead.next_follow_up),
      payment_due_date: toDateInput(lead.payment_due_date), tags: lead.tags || [], custom_fields: lead.custom_fields || {} } : EMPTY);
  }, [open, lead]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const inp = (k, props = {}) => <input data-testid={`lead-form-${k}`} className="crm-input" value={f[k] ?? ""} onChange={(e) => set(k)(e.target.value)} {...props} />;

  const submit = async (e, force = false) => {
    e && e.preventDefault();
    setBusy(true);
    const body = { ...f, force, deal_value: Number(f.deal_value || 0), probability: f.probability === "" || f.probability == null ? null : Number(f.probability),
      collected_amount: Number(f.collected_amount || 0), recurring_value: Number(f.recurring_value || 0), assigned_to: f.assigned_to || null,
      expected_close: f.expected_close ? fromInput(f.expected_close + "T12:00") : null, payment_due_date: f.payment_due_date ? fromInput(f.payment_due_date + "T12:00") : null,
      next_follow_up: fromInput(f.next_follow_up) };
    try {
      const r = lead ? await api.put(`/leads/${lead.id}`, body) : await api.post("/leads", body);
      toast.success(lead ? "Lead updated" : "Lead created");
      onOpenChange(false);
      onSaved && onSaved(r.data);
    } catch (ex) {
      if (ex.response?.status === 409) setDups(ex.response.data.detail.duplicates || []);
      else toast.error(errMsg(ex));
    } finally { setBusy(false); }
  };
  const toggleTag = (t) => set("tags")(f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="lead-form-dialog">
        <DialogHeader><DialogTitle>{lead ? `Edit ${lead.company}` : "Create lead"}</DialogTitle></DialogHeader>
        {dups.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="duplicate-warning">
            <p className="flex items-center gap-2 text-sm font-bold text-amber-800"><AlertTriangle className="h-4 w-4" /> Possible duplicate lead(s) found</p>
            <ul className="mt-2 space-y-1 text-xs text-amber-900">
              {dups.map((d) => <li key={d.id}>• <b>{d.company}</b> — {d.contact_person} · {d.phone} · {d.email} {d.assigned_name && `(owner: ${d.assigned_name})`}</li>)}
            </ul>
            <button type="button" data-testid="duplicate-create-anyway" onClick={() => submit(null, true)} className="mt-2 text-xs font-bold text-amber-800 underline">Create anyway</button>
          </div>
        )}
        <form onSubmit={submit} className="space-y-5">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Client</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Company / client *">{inp("company", { required: true })}</Field>
              <Field label="Contact person">{inp("contact_person")}</Field>
              <Field label="Phone">{inp("phone")}</Field>
              <Field label="Email">{inp("email", { type: "email" })}</Field>
              <Field label="Location">{inp("location")}</Field>
              <Field label="Business type">{inp("business_type")}</Field>
              <Field label="Business vertical"><Sel testid="lead-form-vertical" value={f.vertical} onChange={set("vertical")} options={meta.verticals} /></Field>
              <Field label="Lead source"><Sel testid="lead-form-source" value={f.source} onChange={set("source")} options={meta.sources} /></Field>
              {isAdmin && <Field label="Assigned salesperson"><Sel testid="lead-form-assigned" value={f.assigned_to} onChange={set("assigned_to")} placeholder="Assign to me" options={salesUsers.filter((u) => u.active).map((u) => ({ value: u.id, label: u.name }))} /></Field>}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Opportunity</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Deal name">{inp("deal_name")}</Field>
              <Field label="Estimated deal value (₹)">{inp("deal_value", { type: "number", min: 0 })}</Field>
              <Field label="Probability %">{inp("probability", { type: "number", min: 0, max: 100, placeholder: "auto by stage" })}</Field>
              <Field label="Stage"><Sel testid="lead-form-stage" value={f.stage} onChange={set("stage")} options={meta.stages} /></Field>
              <Field label="Expected closing date">{inp("expected_close", { type: "date" })}</Field>
              <Field label="Next follow-up">{inp("next_follow_up", { type: "datetime-local" })}</Field>
              <Field label="Next action" className="sm:col-span-3">{inp("next_action")}</Field>
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Revenue & collection</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Payment status"><Sel value={f.payment_status} onChange={set("payment_status")} options={PAYMENT_STATUSES} /></Field>
              <Field label="Collection status"><Sel value={f.collection_status} onChange={set("collection_status")} options={COLLECTION_STATUSES} /></Field>
              <Field label="Collected amount (₹)">{inp("collected_amount", { type: "number", min: 0 })}</Field>
              <Field label="Payment due date">{inp("payment_due_date", { type: "date" })}</Field>
              <label className="flex items-center gap-2 pt-5 text-sm font-medium"><input type="checkbox" data-testid="lead-form-recurring" checked={!!f.recurring} onChange={(e) => set("recurring")(e.target.checked)} /> Recurring revenue</label>
              {f.recurring && <Field label="Recurring value / month (₹)">{inp("recurring_value", { type: "number", min: 0 })}</Field>}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Requirements"><textarea data-testid="lead-form-requirements" rows={3} className="crm-input h-auto py-2" value={f.requirements} onChange={(e) => set("requirements")(e.target.value)} /></Field>
            <Field label="Notes"><textarea data-testid="lead-form-notes" rows={3} className="crm-input h-auto py-2" value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></Field>
          </div>
          <div>
            <span className="crm-label">Tags</span>
            <div className="flex flex-wrap gap-2">
              {meta.tags.map((t) => (
                <button type="button" key={t} data-testid={`lead-form-tag-${t}`} onClick={() => toggleTag(t)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${f.tags.includes(t) ? "border-primary bg-primary text-white" : "hover:border-primary"}`}>{t}</button>
              ))}
            </div>
          </div>
          {meta.custom_fields.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Custom fields</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {meta.custom_fields.map((cf) => (
                  <Field key={cf.key} label={cf.label}>
                    <input type={cf.type || "text"} className="crm-input" value={f.custom_fields[cf.key] ?? ""} onChange={(e) => set("custom_fields")({ ...f.custom_fields, [cf.key]: e.target.value })} />
                  </Field>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 border-t pt-4">
            <button type="button" onClick={() => onOpenChange(false)} className="h-9 rounded-md border px-4 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button data-testid="lead-form-submit" disabled={busy} className="h-9 rounded-md bg-primary px-5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">{busy ? "Saving…" : lead ? "Save changes" : "Create lead"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
